import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { CANVAS_HEIGHT, CANVAS_WIDTH, type Layer } from '@liveforge/shared';

/**
 * Direct manipulation of the selected layer, over the preview.
 *
 * **Selection is not done here.** A layer is chosen in the layer list and only
 * there, which removes the whole hit-testing problem - inverting every layer's
 * rotation to find what is under the cursor, in z-order - and with it the
 * class of bug where a click lands on something the director did not mean.
 *
 * Everything is expressed as a percentage of the frame, so the overlay needs
 * to know nothing about how large the monitor is being displayed. Pointer
 * deltas are the one exception: they arrive in screen pixels and are scaled
 * back into the 1920x1080 space the layer tree is authored in.
 */

/** Corner and edge handles, as fractions of the box. */
const HANDLES = [
  { id: 'nw', x: 0, y: 0 },
  { id: 'n', x: 0.5, y: 0 },
  { id: 'ne', x: 1, y: 0 },
  { id: 'e', x: 1, y: 0.5 },
  { id: 'se', x: 1, y: 1 },
  { id: 's', x: 0.5, y: 1 },
  { id: 'sw', x: 0, y: 1 },
  { id: 'w', x: 0, y: 0.5 },
] as const;

const MIN_SIZE = 24;

interface DragState {
  kind: 'move' | 'resize' | 'rotate';
  handle: (typeof HANDLES)[number] | null;
  startX: number;
  startY: number;
  layer: Layer;
  /** Logical units per screen pixel, fixed at the start of the gesture. */
  scale: number;
  centreClientX: number;
  centreClientY: number;
}

function rotate(x: number, y: number, degrees: number): { x: number; y: number } {
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return { x: x * cos - y * sin, y: x * sin + y * cos };
}

export interface LayerTransformProps {
  layer: Layer | undefined;
  onChange: (patch: Partial<Layer>) => void;
}

export function LayerTransform({ layer, onChange }: LayerTransformProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef<DragState | null>(null);

  if (!layer) {
    return null;
  }

  function begin(
    event: ReactPointerEvent<HTMLElement>,
    kind: DragState['kind'],
    handle: (typeof HANDLES)[number] | null,
  ) {
    if (!layer || layer.locked) return;
    const root = rootRef.current;
    if (!root) return;

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);

    const rect = root.getBoundingClientRect();
    drag.current = {
      kind,
      handle,
      startX: event.clientX,
      startY: event.clientY,
      layer,
      scale: CANVAS_WIDTH / rect.width,
      centreClientX: rect.left + ((layer.x + layer.width / 2) / CANVAS_WIDTH) * rect.width,
      centreClientY: rect.top + ((layer.y + layer.height / 2) / CANVAS_HEIGHT) * rect.height,
    };
  }

  function move(event: ReactPointerEvent<HTMLElement>) {
    const state = drag.current;
    if (!state) return;

    const start = state.layer;

    if (state.kind === 'rotate') {
      const angle =
        (Math.atan2(event.clientY - state.centreClientY, event.clientX - state.centreClientX) *
          180) /
          Math.PI +
        90;
      // Whole degrees: the properties panel shows an integer, and a rotation
      // carried to six decimal places is noise on the wire.
      onChange({ rotation: Math.round(angle) });
      return;
    }

    const dx = (event.clientX - state.startX) * state.scale;
    const dy = (event.clientY - state.startY) * state.scale;

    if (state.kind === 'move') {
      onChange({ x: Math.round(start.x + dx), y: Math.round(start.y + dy) });
      return;
    }

    const handle = state.handle;
    if (!handle) return;

    const signX = handle.x === 1 ? 1 : handle.x === 0 ? -1 : 0;
    const signY = handle.y === 1 ? 1 : handle.y === 0 ? -1 : 0;

    // Alt turns a resize into a crop, as OBS does: the box stays exactly where
    // it is and the edge eats into the source instead. Only feeds carry a crop,
    // so on a shape or a caption Alt is simply ignored rather than doing
    // something surprising.
    if (event.altKey && (start.type === 'video' || start.type === 'screenshare')) {
      const local = rotate(dx, dy, -start.rotation);
      const crop = start.crop ?? { x: 0, y: 0, width: 1, height: 1 };
      const fx = local.x / start.width;
      const fy = local.y / start.height;

      let { x, y, width, height } = crop;
      if (signX === 1) width += fx;
      if (signX === -1) {
        x += fx;
        width -= fx;
      }
      if (signY === 1) height += fy;
      if (signY === -1) {
        y += fy;
        height -= fy;
      }

      // A crop is a fraction of the source, so everything stays inside 0..1 and
      // never collapses to a sliver there is no way to drag back out of.
      x = Math.min(Math.max(x, 0), 0.9);
      y = Math.min(Math.max(y, 0), 0.9);
      width = Math.min(Math.max(width, 0.1), 1 - x);
      height = Math.min(Math.max(height, 0.1), 1 - y);

      onChange({ crop: { x, y, width, height } } as Partial<Layer>);
      return;
    }

    // Resizing happens in the layer's own axes, so a rotated layer grows
    // towards the handle the director is pulling rather than towards the
    // screen's idea of "right".
    const local = rotate(dx, dy, -start.rotation);

    let width = Math.max(MIN_SIZE, start.width + local.x * signX);
    let height = Math.max(MIN_SIZE, start.height + local.y * signY);

    // A corner keeps the shape; an edge is how you deliberately change it.
    // Dragging a corner of a camera and getting a squashed face is never what
    // anyone meant, and the two gestures are already distinct. Shift lets go
    // of the constraint for the rare time it is wanted.
    const corner = signX !== 0 && signY !== 0;
    if (corner && !event.shiftKey && start.height > 0) {
      const aspect = start.width / start.height;
      // Whichever axis the pointer moved further in leads, so the box follows
      // the hand rather than snapping back along one axis.
      if (Math.abs(width - start.width) >= Math.abs(height - start.height) * aspect) {
        height = Math.max(MIN_SIZE, width / aspect);
        width = height * aspect;
      } else {
        width = Math.max(MIN_SIZE, height * aspect);
        height = width / aspect;
      }
    }

    // The opposite edge stays put, so the centre moves by half of the change.
    const shift = rotate(
      (signX * (width - start.width)) / 2,
      (signY * (height - start.height)) / 2,
      start.rotation,
    );
    const centreX = start.x + start.width / 2 + shift.x;
    const centreY = start.y + start.height / 2 + shift.y;

    onChange({
      x: Math.round(centreX - width / 2),
      y: Math.round(centreY - height / 2),
      width: Math.round(width),
      height: Math.round(height),
    });
  }

  function end(event: ReactPointerEvent<HTMLElement>) {
    if (!drag.current) return;
    drag.current = null;
    event.currentTarget.releasePointerCapture(event.pointerId);
  }

  const style = {
    left: `${(layer.x / CANVAS_WIDTH) * 100}%`,
    top: `${(layer.y / CANVAS_HEIGHT) * 100}%`,
    width: `${(layer.width / CANVAS_WIDTH) * 100}%`,
    height: `${(layer.height / CANVAS_HEIGHT) * 100}%`,
    transform: `rotate(${layer.rotation}deg)`,
  };

  return (
    // Not interactive itself: clicks fall through to the monitor, because
    // selection lives in the layer list and nothing here should steal it.
    <div className="layer-transform" ref={rootRef}>
      <div
        className={`layer-transform__box ${layer.locked ? 'layer-transform__box--locked' : ''}`}
        style={style}
        onPointerDown={(event) => begin(event, 'move', null)}
        onPointerMove={move}
        onPointerUp={end}
        onPointerCancel={end}
      >
        {layer.locked ? null : (
          <>
            {HANDLES.map((handle) => (
              <span
                key={handle.id}
                className={`layer-transform__handle layer-transform__handle--${handle.id}`}
                onPointerDown={(event) => begin(event, 'resize', handle)}
                onPointerMove={move}
                onPointerUp={end}
                onPointerCancel={end}
              />
            ))}
            <span
              className="layer-transform__rotate"
              title="Drag to rotate"
              onPointerDown={(event) => begin(event, 'rotate', null)}
              onPointerMove={move}
              onPointerUp={end}
              onPointerCancel={end}
            />
          </>
        )}
      </div>
    </div>
  );
}
